import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faKey, faShieldHalved, faMobileScreen } from '@fortawesome/pro-solid-svg-icons'
import { avatarColour, initialsOf, methodHint } from './account-look'
import { BRAND_ICONS } from './brand-icons'
import { IconSVG } from '../sso-workspace/provider-logo'
import './recent-accounts.scss'

// A remembered account's pieces, drawn in the sign-in card's language
// (auth-card.scss, .sa-option rows): the round avatar, and its name, email
// and last-method line. The chooser (login-email's "Choose an account") and
// the sign-out offers (forget-account.js) both use them.

// The picture when one is known (https only, from recent-accounts.js), else
// the initials on the email's colour. Decorative: the row says who it is.
export const AccountAvatar = ({ account }) => {
  const [broken, updateBroken] = useState(false)

  if (account && account.avatarURL && !broken) {
    return <img className="sa-avatar" src={account.avatarURL} alt="" referrerPolicy="no-referrer" onError={() => updateBroken(true)} />
  }

  return (
    <span className="sa-avatar" style={{ '--sa-avatar-bg': avatarColour(account && account.email) }} aria-hidden="true">
      {initialsOf(account || {})}
    </span>
  )
}

const HINT_ICONS = { password: faKey, sso: faShieldHalved, device: faMobileScreen }

// A single sign-on entry's brand mark, from the entry's small iconKey and
// the library copy (brand-icons.js), the way ProviderLogo draws a connection:
// the light mark, and the dark one in dark mode when the mark has one. null
// without a known key (an older entry): the shield stands in.
const brandOf = iconKey => (iconKey && Object.prototype.hasOwnProperty.call(BRAND_ICONS, iconKey) ? BRAND_ICONS[iconKey] : null)

export const BrandIcon = ({ iconKey }) => {
  const brand = brandOf(iconKey)
  if (!brand) return null
  return (
    <span className={`sa-brand${brand.dark ? ' sa-brand-has-dark' : ''}`} aria-hidden="true" data-testid="sa-brand" data-icon-key={iconKey}>
      <IconSVG svg={brand.light} className="sa-brand-svg sa-brand-light" />
      {brand.dark && <IconSVG svg={brand.dark} className="sa-brand-svg sa-brand-dark" />}
    </span>
  )
}

// "Last used: EdXR Azure Dev", with the method's small icon: the
// connection's brand mark for single sign-on when known, else the shield.
export const AccountHint = ({ account }) => {
  const hint = methodHint(account)
  if (!hint) return null
  const icon = HINT_ICONS[account.method.kind]
  const connection = account.method.kind === 'sso' && account.method.connection
  const hasBrand = !!(connection && brandOf(connection.iconKey))

  return (
    <span className="sa-account-hint">
      {hasBrand ? <BrandIcon iconKey={connection.iconKey} /> : (icon && <FontAwesomeIcon icon={icon} aria-hidden="true" data-testid="sa-hint-icon" />)}
      <span>
        <span className="visually-hidden">Last used: </span>
        {hint}
      </span>
    </span>
  )
}

// The row's title: the name, else the email.
export const accountTitle = account => (account && (account.name || account.email)) || ''

// The title as drawn: one line, cut short with an ellipsis (a long email
// mustn't break mid-word on a phone), the whole of it on hover.
export const AccountTitle = ({ account }) =>
  <span className="sa-account-clip" title={accountTitle(account)}>{accountTitle(account)}</span>

// Under the title: the email (when the title is the name) and the hint.
export const AccountSub = ({ account }) =>
  <React.Fragment>
    {account.name && <span className="sa-account-email" title={account.email}>{account.email}</span>}
    <AccountHint account={account} />
  </React.Fragment>
